import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useShop } from '../../../shared/store';

const CartPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, cartSubtotal, removeFromCart, updateCartQuantity } = useShop();
  const deliveryFee = 1500;
  const checkoutEditionStorageKey = 'haapa.checkout.edition';
  const checkoutPodProjectStorageKey = 'haapa.checkout.podProjectId';

  if (cart.length === 0) {
    return (
      <section className="animate-fade-up rounded-3xl border border-orange-100 bg-white p-8 text-center shadow-lg">
        <h2 className="text-2xl font-semibold text-slate-900">{t('cart.emptyTitle')}</h2>
        <p className="mt-2 text-sm text-slate-600">{t('cart.emptySubtitle')}</p>
        <Link
          to="/products"
          className="mt-4 inline-flex rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          {t('cart.browseProducts')}
        </Link>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <header className="animate-fade-up">
        <h2 className="text-3xl font-semibold text-orange-700">{t('cart.title')}</h2>
        <p className="mt-2 text-sm text-slate-600">{t('cart.subtitle')}</p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          {cart.map((cartItem, index) => (
            <article
              key={cartItem.product.id}
              className="animate-fade-up rounded-2xl border border-orange-100 bg-white p-4 shadow-sm"
              style={{ animationDelay: `${index * 60}ms` }}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <img
                  src={cartItem.product.imageUrl}
                  alt={t(cartItem.product.nameKey)}
                  className="h-24 w-24 rounded-xl object-cover"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900">{t(cartItem.product.nameKey)}</h3>
                  <p className="text-sm font-semibold text-orange-600">$ {cartItem.product.price.toFixed(2)}</p>
                </div>
                <label className="grid gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t('cart.quantity')}
                  <input
                    type="number"
                    min={1}
                    value={cartItem.quantity}
                    onChange={(event) =>
                      updateCartQuantity(cartItem.product.id, Math.max(1, Number(event.target.value) || 1))
                    }
                    className="w-20 rounded-lg border border-orange-200 px-2 py-2 text-sm text-slate-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeFromCart(cartItem.product.id)}
                  className="rounded-lg border border-orange-200 px-3 py-2 text-sm font-semibold text-orange-600 transition hover:border-orange-300 hover:text-orange-700"
                >
                  {t('cart.remove')}
                </button>
              </div>
            </article>
          ))}
        </div>

        <aside className="animate-fade-up rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-orange-700">{t('cart.summary')}</h3>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-slate-500">{t('cart.subtotal')}</dt>
              <dd className="font-semibold text-orange-700">$ {cartSubtotal.toFixed(2)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-slate-500">{t('cart.shipping')}</dt>
              <dd className="font-semibold text-orange-700">
                $ {deliveryFee.toFixed(0)}
              </dd>
            </div>
            <div className="flex items-center justify-between border-t border-orange-100 pt-2">
              <dt className="text-slate-700">{t('cart.total')}</dt>
              <dd className="text-base font-bold text-orange-700">
                $ {(cartSubtotal + deliveryFee).toFixed(2)}
              </dd>
            </div>
          </dl>
          <button
            type="button"
            onClick={() => {
              const edition = window.sessionStorage.getItem(checkoutEditionStorageKey) as 'digital' | 'print' | null;
              const podProjectId = window.sessionStorage.getItem(checkoutPodProjectStorageKey) ?? undefined;
              void navigate('/checkout', {
                state: edition || podProjectId ? { ...(edition ? { edition } : {}), ...(podProjectId ? { podProjectId } : {}) } : undefined,
              });
            }}
            className="mt-4 w-full rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            {t('cart.proceedCheckout')}
          </button>
        </aside>
      </div>
    </section>
  );
};

export default CartPage;
